import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface SupportLauncherControl {
  readonly hidden: boolean;
  /** Hides the launcher until the returned release function is called. */
  readonly hold: () => () => void;
  /** Phones: a full-screen step (open conversation) hides the bottom nav while it is held. */
  readonly navHidden: boolean;
  readonly holdNav: () => () => void;
}

const SupportLauncherContext = createContext<SupportLauncherControl | null>(null);

/**
 * QA: the fixed «تحدث مع الدعم» launcher sat on top of the inbox composer's send button on
 * phones. Screens that pin their own controls to the bottom of the viewport hold the launcher
 * hidden while they are on screen (see `useHideSupportLauncher`).
 */
export function SupportLauncherProvider({ children }: { readonly children: ReactNode }) {
  const [holds, setHolds] = useState(0);
  const hold = useCallback(() => {
    setHolds((n) => n + 1);
    return () => setHolds((n) => Math.max(0, n - 1));
  }, []);
  const [navHolds, setNavHolds] = useState(0);
  const holdNav = useCallback(() => {
    setNavHolds((n) => n + 1);
    return () => setNavHolds((n) => Math.max(0, n - 1));
  }, []);
  const value = useMemo(
    () => ({ hidden: holds > 0, hold, navHidden: navHolds > 0, holdNav }),
    [holds, hold, navHolds, holdNav],
  );
  return (
    <SupportLauncherContext.Provider value={value}>{children}</SupportLauncherContext.Provider>
  );
}

/** Hides the floating support launcher while `active` is true (no-op outside the provider). */
export function useHideSupportLauncher(active: boolean): void {
  const hold = useContext(SupportLauncherContext)?.hold;
  useEffect(() => {
    if (!active || !hold) return;
    return hold();
  }, [active, hold]);
}

/**
 * Hides the phone bottom nav while `active` is true (no-op outside the provider). The open
 * conversation is a full-screen step: its composer owns the bottom edge and the safe area.
 */
export function useHideBottomNav(active: boolean): void {
  const holdNav = useContext(SupportLauncherContext)?.holdNav;
  useEffect(() => {
    if (!active || !holdNav) return;
    return holdNav();
  }, [active, holdNav]);
}

/** True while a full-screen step asked for the bottom nav to be hidden. */
export function useBottomNavHidden(): boolean {
  return useContext(SupportLauncherContext)?.navHidden ?? false;
}

/** True while some screen asked for the floating support launcher to be hidden. */
export function useSupportLauncherHidden(): boolean {
  return useContext(SupportLauncherContext)?.hidden ?? false;
}
